"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { useCombobox } from "downshift";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useDebouncedCallback } from "use-debounce";
import { cn } from "../../utils/cn.js";
import { TRANSFORMER_MAX_MOBILE_WIDTH_PX } from "../../utils/constants.js";
import { Drawer } from "../Drawer/index.js";
import { IconButton } from "../IconButton/index.js";
import { InputText } from "../InputText/index.js";
import { overlayInputVariants } from "../OverlaySearchHeader/OverlayInput/OverlayInput.variants.js";
import { ScrollArea } from "../ScrollArea/index.js";
import { autocompleteVariants } from "./Autocomplete.variant.js";
import { AutocompleteContainer } from "./AutocompleteContainer.js";
import FilterChipBar from "./FilterChipBar/FilterChipBar.js";
function isIOSBrowser() {
    if (void 0 === globalThis.window) return false;
    const ua = globalThis.window.navigator?.userAgent;
    const iOS = /iPhone/i.exec(ua);
    return iOS?.length;
}
const Autocomplete_Autocomplete = function({ autocompleteData, handleInputChange, handleSelectItem, handleOnMenuOpen, initialSelectedItem, name, iconName = 'search', label = 'Where to?', className, errorMessage, inputRef, closeMenuOnSelect = true, updateInputValueOnSelect = true, required = false, filterChipData = [], handleFilterChipClick, selectedFilterChipData = new Set(), forceFocusAfterRender = false, iosKeyboardHackTimeout = 300 }) {
    const [items, setItems] = useState([
        ...autocompleteData
    ]);
    const [selectedItem, setSelectedItem] = useState(initialSelectedItem ?? null);
    const [highlightedIndexWithKeyArrow, setHighlightedIndexWithKeyArrow] = useState(-1);
    const wrapperClassName = cn('relative flex flex-col gap-1', className);
    useEffect(()=>{
        setItems([
            ...autocompleteData
        ]);
    }, [
        autocompleteData
    ]);
    const showDrawerOverlay = useMediaQuery({
        maxWidth: TRANSFORMER_MAX_MOBILE_WIDTH_PX
    });
    const [isClientSide, setIsClientSide] = useState(false);
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    useEffect(()=>{
        setIsClientSide(true);
    }, []);
    const isIOS = isIOSBrowser();
    const { isOpen, getMenuProps, getInputProps, highlightedIndex, getItemProps, closeMenu, setHighlightedIndex } = useCombobox({
        initialSelectedItem: initialSelectedItem ?? null,
        initialInputValue: initialSelectedItem?.itemName ?? '',
        onInputValueChange ({ inputValue }) {
            if ('' === inputValue) setSelectedItem(null);
            handleInputChange(inputValue);
        },
        onSelectedItemChange ({ selectedItem }) {
            if (selectedItem && handleSelectItem) handleSelectItem(selectedItem);
            setSelectedItem(selectedItem);
        },
        onHighlightedIndexChange: (event)=>{
            event.type === useCombobox.stateChangeTypes.InputKeyDownArrowDown || event.type === useCombobox.stateChangeTypes.InputKeyDownArrowUp ? setHighlightedIndexWithKeyArrow(event.highlightedIndex) : setHighlightedIndexWithKeyArrow(-1);
        },
        onIsOpenChange: (changes)=>{
            setIsOverlayOpen(changes.isOpen);
            if (changes.isOpen) {
                if ('function' == typeof handleOnMenuOpen) handleOnMenuOpen();
                if (isIOS && showDrawerOverlay) setTimeout(()=>{
                    const overlayInput = document.querySelector('input[data-testid="autocomplete-input-in-overlay"]');
                    if (overlayInput) overlayInput.focus();
                }, iosKeyboardHackTimeout);
                if (forceFocusAfterRender && showDrawerOverlay && !isIOS) setTimeout(()=>{
                    const mobileInput = document.querySelector('input[data-testid="autocomplete-input-in-overlay"]');
                    if (mobileInput) mobileInput.focus();
                }, 0);
            }
        },
        items,
        itemToString (item) {
            return item ? item.itemName : '';
        },
        stateReducer: (state, actionAndChanges)=>{
            const { changes, type } = actionAndChanges;
            const isSelectAction = type === useCombobox.stateChangeTypes.InputKeyDownEnter || type === useCombobox.stateChangeTypes.InputClick || type === useCombobox.stateChangeTypes.ItemClick;
            if (!updateInputValueOnSelect && isSelectAction) changes.inputValue = state.inputValue;
            if (!closeMenuOnSelect && isSelectAction) changes.isOpen = true;
            if (changes.isOpen && !state.isOpen && items.length > 0) changes.highlightedIndex = 0;
            if (type === useCombobox.stateChangeTypes.InputClick && !state.isOpen && isIOS) return {
                ...changes,
                isOpen: true,
                highlightedIndex: items.length > 0 ? 0 : changes.highlightedIndex
            };
            if (type === useCombobox.stateChangeTypes.InputClick && state.isOpen) return {
                ...changes,
                isOpen: true
            };
            if (type === useCombobox.stateChangeTypes.InputBlur && isOverlayOpen && showDrawerOverlay) return {
                ...changes,
                inputValue: state.inputValue,
                selectedItem: state.selectedItem,
                highlightedIndex: state.highlightedIndex,
                isOpen: true
            };
            return changes;
        }
    });
    const { icon } = overlayInputVariants();
    const { listWrapperSlot, overlayInputSlot } = autocompleteVariants({
        isHidden: !(isOpen && items.length > 0),
        isDrawer: showDrawerOverlay
    });
    const listWrapperRef = useRef(null);
    useEffect(()=>{
        const handleClickOutside = (e)=>{
            if (!showDrawerOverlay && listWrapperRef.current && !listWrapperRef.current.contains(e.target)) closeMenu();
        };
        document.addEventListener('mousedown', handleClickOutside);
        return ()=>document.removeEventListener('mousedown', handleClickOutside);
    }, [
        closeMenu,
        showDrawerOverlay
    ]);
    useEffect(()=>{
        if (isOpen && items.length > 0) items[0].isGroupHeader && items.length > 1 ? setHighlightedIndex(1) : setHighlightedIndex(0);
    }, [
        items,
        isOpen,
        setHighlightedIndex
    ]);
    const shouldShowError = errorMessage && !isOpen;
    const handleResize = useCallback(()=>{
        const viewport = window.visualViewport;
        if (!viewport) return;
        document.documentElement.style.setProperty('--vvh', `${viewport.height}px`);
    }, []);
    const onResize = useDebouncedCallback(handleResize, 300);
    useEffect(()=>{
        const viewport = window.visualViewport;
        if (!viewport) return;
        onResize();
        viewport.addEventListener('resize', onResize);
        return ()=>{
            viewport?.removeEventListener('resize', onResize);
        };
    }, [
        onResize
    ]);
    return /*#__PURE__*/ jsxs("div", {
        className: wrapperClassName,
        ref: listWrapperRef,
        children: [
            /*#__PURE__*/ jsx(InputText, {
                iconLeft: iconName ?? void 0,
                isInset: true,
                label: label,
                "data-testid": "autocomplete-input",
                name: name,
                errorMessage: shouldShowError ? errorMessage : void 0,
                hasExternalError: Boolean(shouldShowError),
                required: required,
                ...getInputProps({
                    ref: inputRef,
                    onClick: (e)=>{
                        e.currentTarget.select();
                    }
                })
            }),
            showDrawerOverlay && isClientSide ? isOverlayOpen && /*#__PURE__*/ jsxs(Drawer, {
                title: "autocomplete",
                showCloseButton: false,
                showDragHandle: false,
                open: true,
                snapPoints: [
                    1
                ],
                repositionInputs: false,
                scroll: "body",
                classNames: {
                    contentSlot: 'p-0'
                },
                onNaturalClose: ()=>{
                    setIsOverlayOpen(false);
                    closeMenu();
                },
                children: [
                    /*#__PURE__*/ jsxs("div", {
                        className: overlayInputSlot(),
                        children: [
                            /*#__PURE__*/ jsx("div", {
                                className: icon(),
                                children: /*#__PURE__*/ jsx(IconButton, {
                                    onClick: ()=>{
                                        setIsOverlayOpen(false);
                                        closeMenu();
                                    },
                                    iconName: "arrow_back",
                                    type: "plainPrimary",
                                    shadow: true,
                                    size: "md",
                                    "aria-label": "Close Drawer"
                                })
                            }),
                            /*#__PURE__*/ jsx(InputText, {
                                iconLeft: iconName ?? void 0,
                                isInset: true,
                                label: label,
                                "data-testid": "autocomplete-input-in-overlay",
                                name: name,
                                errorMessage: void 0,
                                hasExternalError: false,
                                ...getInputProps({
                                    autoFocus: !isIOS,
                                    id: 'overlayInput',
                                    ref: inputRef,
                                    onClick: (e)=>{
                                        e.currentTarget.select();
                                    }
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ jsx("ul", {
                        ...getMenuProps(),
                        className: cn(listWrapperSlot(), 'h-full max-h-[var(--vvh)]'),
                        children: /*#__PURE__*/ jsxs(ScrollArea, {
                            popoverBody: true,
                            slots: {
                                root: 'max-w-full h-full',
                                content: 'p-1 gap-0'
                            },
                            children: [
                                filterChipData.length > 0 && 'function' == typeof handleFilterChipClick && /*#__PURE__*/ jsx(FilterChipBar, {
                                    filterChipData: filterChipData,
                                    handleFilterChipClick: handleFilterChipClick,
                                    selectedFilterChipData: selectedFilterChipData
                                }),
                                /*#__PURE__*/ jsx(AutocompleteContainer, {
                                    items: items,
                                    isOpen: isOpen,
                                    highlightedIndex: highlightedIndex,
                                    highlightedIndexWithKeyArrow: highlightedIndexWithKeyArrow,
                                    selectedItem: selectedItem,
                                    getItemProps: getItemProps
                                })
                            ]
                        })
                    })
                ]
            }) : /*#__PURE__*/ jsx("ul", {
                ...getMenuProps(),
                className: cn(listWrapperSlot()),
                children: /*#__PURE__*/ jsxs(ScrollArea, {
                    slots: {
                        root: 'max-w-full h-full',
                        content: 'p-1 gap-0 overflow-x-hidden'
                    },
                    children: [
                        filterChipData.length > 0 && 'function' == typeof handleFilterChipClick && /*#__PURE__*/ jsx(FilterChipBar, {
                            filterChipData: filterChipData,
                            handleFilterChipClick: handleFilterChipClick,
                            selectedFilterChipData: selectedFilterChipData
                        }),
                        /*#__PURE__*/ jsx(AutocompleteContainer, {
                            items: items,
                            isOpen: isOpen,
                            highlightedIndex: highlightedIndex,
                            highlightedIndexWithKeyArrow: highlightedIndexWithKeyArrow,
                            selectedItem: selectedItem,
                            getItemProps: getItemProps
                        })
                    ]
                })
            })
        ]
    });
};
export { Autocomplete_Autocomplete as Autocomplete };
